// Expandable instructions for Device & Athlete assignment — portaled so the modal never clips it.

import React, { JSX, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const POPUP_Z = 100;

export default function AssignmentInstructionsHelp(): JSX.Element {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const updatePosition = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const popupWidth = Math.min(440, window.innerWidth - 16);
    const margin = 8;
    let left = rect.right - popupWidth;
    left = Math.max(margin, Math.min(left, window.innerWidth - popupWidth - margin));
    const top = rect.bottom + 8;
    setPos({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => updatePosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;

    function onDocMouseDown(e: MouseEvent): void {
      const t = e.target as Node;
      if (anchorRef.current?.contains(t)) return;
      const portal = document.querySelector('[data-assignment-instructions-popup="true"]');
      if (portal?.contains(t)) return;
      setOpen(false);
    }

    function onKeyDown(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={anchorRef} className="relative inline-flex shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-900 shadow-sm transition-colors hover:bg-blue-100 dark:border-blue-700/60 dark:bg-blue-950/40 dark:text-blue-100 dark:hover:bg-blue-900/50"
      >
        <span className="text-blue-700" aria-hidden>
          ℹ
        </span>
        How assignment works
      </button>

      {open && pos
        ? createPortal(
          <div
            data-assignment-instructions-popup="true"
            className="max-h-[min(78vh,620px)] w-[min(480px,calc(100vw-1rem))] overflow-hidden overflow-y-auto rounded-xl border border-blue-200 dark:border-blue-700/60 bg-white dark:bg-slate-900 shadow-2xl"
            style={{
              position: 'fixed',
              top: pos.top,
              left: pos.left,
              zIndex: POPUP_Z,
            }}
          >
            <div className="border-b border-blue-100 dark:border-blue-900/60 bg-gradient-to-r from-blue-50 via-sky-50 to-white dark:from-slate-800 dark:via-slate-900 dark:to-slate-950 px-4 py-3">
              <div className="text-sm font-semibold text-gray-900 dark:text-slate-100">Device &amp; athlete assignment</div>
              <p className="mt-1 text-xs text-gray-600 dark:text-slate-400">
                Add devices to the event, then assign athletes to those devices.
              </p>
            </div>

            <div className="space-y-4 p-4 text-sm text-gray-800 dark:text-slate-200">
              <section className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 dark:border-amber-700/60 dark:bg-amber-950/40">
                <div className="text-xs font-semibold uppercase tracking-wide text-amber-900 dark:text-amber-100">
                  Before you start
                </div>
                <p className="mt-1.5 text-xs leading-relaxed text-amber-950 dark:text-amber-100">
                  Use the assignment screen to scan athletes and devices. Rear device IDs must exist in the{' '}
                  <span className="font-semibold">device mappings table</span> (front devices are not checked against rear validation). On{' '}
                  <span className="font-semibold">Submit</span>, the event device list is synced to the Device ID
                  column (adds and removals), then assignments are saved.
                </p>
              </section>

              <section>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                  Assign athletes
                </div>
                <ol className="list-none space-y-3">
                  <li className="flex gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-800">
                      1
                    </span>
                    <span className="min-w-0 pt-0.5 text-xs leading-relaxed">
                      <span className="font-semibold text-gray-900 dark:text-slate-100">Upload CSV.</span> See the required format by
                      hovering over the <span className="font-semibold">Upload CSV</span> button.
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-800">
                      2
                    </span>
                    <span className="min-w-0 pt-0.5 text-xs leading-relaxed">
                      Scan or type an athlete&apos;s name or ID into the{' '}
                      <span className="font-semibold">Current Athlete ID</span> field.
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-800">
                      3
                    </span>
                    <span className="min-w-0 pt-0.5 text-xs leading-relaxed">
                      Scan the device ID you want to assign to this athlete.
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-800">
                      4
                    </span>
                    <span className="min-w-0 pt-0.5 text-xs leading-relaxed">
                      Repeat for all other athletes.
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-800">
                      5
                    </span>
                    <span className="min-w-0 pt-0.5 text-xs leading-relaxed">
                      <span className="font-semibold text-gray-900 dark:text-slate-100">Submit</span> the assignments file.
                    </span>
                  </li>
                </ol>
              </section>
            </div>
          </div>,
          document.body,
        )
        : null}
    </div>
  );
}
